import type { PatientGlobalSearchHit, PatientGlobalSearchResource } from './types';

export interface PatientGlobalSearchGroup {
  resource: PatientGlobalSearchResource;
  label: string;
  hits: PatientGlobalSearchHit[];
}

export interface SearchPatientGlobalHitsOptions {
  hits: readonly PatientGlobalSearchHit[];
  query: string;
  limit?: number;
}

const DEFAULT_RESULT_LIMIT = 50;

const RESOURCE_LABELS: Record<PatientGlobalSearchResource, string> = {
  'phr:conditions': 'Conditions',
  'phr:labs': 'Labs',
  'phr:medications': 'Medications',
  'phr:notes': 'Notes',
  'phr:procedures': 'Procedures',
  'phr:vitals': 'Vitals',
  'rx:prescriptions': 'DoseSpot prescriptions',
  'care-state:lab-orders': 'Ordered through Lotus Health',
};

const RESOURCE_ORDER: readonly PatientGlobalSearchResource[] = [
  'phr:conditions',
  'phr:labs',
  'care-state:lab-orders',
  'phr:medications',
  'rx:prescriptions',
  'phr:vitals',
  'phr:procedures',
  'phr:notes',
];

function normalize(value: string): string {
  return value.trim().toLowerCase();
}

function computeScore(hit: PatientGlobalSearchHit, normalizedQuery: string): number {
  if (normalizedQuery === '') {
    return 1;
  }

  const title = normalize(hit.title);
  const subtitle = normalize(hit.subtitle);
  const keywords = normalize(hit.keywords);
  let score = 0;

  if (title === normalizedQuery) {
    score += 200;
  }

  if (title.startsWith(normalizedQuery)) {
    score += 40;
  } else if (title.includes(normalizedQuery)) {
    score += 24;
  }

  if (subtitle.includes(normalizedQuery)) {
    score += 8;
  }

  if (keywords.includes(normalizedQuery)) {
    score += 4;
  }

  return score;
}

function compareHits(a: PatientGlobalSearchHit, b: PatientGlobalSearchHit): number {
  const aTs = a.sortTimestampMs ?? Number.NEGATIVE_INFINITY;
  const bTs = b.sortTimestampMs ?? Number.NEGATIVE_INFINITY;
  if (aTs !== bTs) {
    return bTs - aTs;
  }

  const resourceDelta = RESOURCE_ORDER.indexOf(a.resource) - RESOURCE_ORDER.indexOf(b.resource);
  if (resourceDelta !== 0) {
    return resourceDelta;
  }

  const titleDelta = a.title.localeCompare(b.title);
  if (titleDelta !== 0) {
    return titleDelta;
  }

  return a.id.localeCompare(b.id);
}

export function searchPatientGlobalHits({
  hits,
  query,
  limit = DEFAULT_RESULT_LIMIT,
}: SearchPatientGlobalHitsOptions): PatientGlobalSearchHit[] {
  const normalizedQuery = normalize(query);
  const scoredHits = hits
    .map((hit) => ({
      hit,
      score: computeScore(hit, normalizedQuery),
    }))
    .filter((item) => item.score > 0)
    .sort((a, b) => {
      if (a.score !== b.score) {
        return b.score - a.score;
      }
      return compareHits(a.hit, b.hit);
    })
    .slice(0, limit);

  return scoredHits.map((item) => item.hit);
}

export function groupPatientGlobalSearchHits(
  hits: readonly PatientGlobalSearchHit[]
): PatientGlobalSearchGroup[] {
  const groupedByResource = hits.reduce<
    Record<PatientGlobalSearchResource, PatientGlobalSearchHit[]>
  >(
    (acc, hit) => {
      acc[hit.resource] = [...acc[hit.resource], hit];
      return acc;
    },
    {
      'phr:conditions': [],
      'phr:labs': [],
      'phr:medications': [],
      'phr:notes': [],
      'phr:procedures': [],
      'phr:vitals': [],
      'rx:prescriptions': [],
      'care-state:lab-orders': [],
    }
  );

  return RESOURCE_ORDER.reduce<PatientGlobalSearchGroup[]>((groups, resource) => {
    const resourceHits = groupedByResource[resource];
    if (resourceHits.length === 0) {
      return groups;
    }

    return [
      ...groups,
      {
        resource,
        label: RESOURCE_LABELS[resource],
        hits: [...resourceHits].sort(compareHits),
      },
    ];
  }, []);
}
